<script lang="ts" setup>
import type { CartItem } from '@/lib/types/Cart'
import formatMoney from '@/lib/formatters/money'
import { formatProductTitle } from '@/lib/formatters/product-title'
import { computed } from 'vue'
import IconBin from '@/icons/bin.svg?component'
import QuantityControl from './QuantityControl.vue'
import type { Product } from '@/lib/types/Product'

const props = defineProps<{
  item: CartItem
  isBusy: boolean
  subscriptionData: Product | null
  dictionary: Record<string, string>
}>()

const emit = defineEmits<{
  (e: 'subscribe', key: string, sellingPlanId: string): void
  (e: 'remove', key: string): void
  (e: 'changeQuantity', key: string, quantity: number): void
  (e: 'changeSellingPlan', key: string, sellingPlanId: string | null): void
}>()

const updateQuantity = (quantity: number) => {
  emit('changeQuantity', props.item.key, quantity)
}

const updateSellingPlan = (sellingPlanId: string) => {
  emit(
    'changeSellingPlan',
    props.item.key,
    sellingPlanId === 'NONE' ? null : sellingPlanId,
  )
}

const isSubscription = computed(() => props.item.selling_plan_allocation)

const subscriptionOptions = computed(
  () =>
    props.subscriptionData?.variants[props.item.variant_id]
      .sellingPlanAllocations,
)

const savePercent = computed(
  () =>
    (subscriptionOptions.value &&
      subscriptionOptions.value.length > 0 &&
      subscriptionOptions.value.reduce(
        (accum, spa) =>
          Math.max(accum, spa.sellingPlan.price_adjustments[0].value),
        0,
      )) ||
    0,
)

const t = (label: string) => {
  return props.dictionary[label] || label
}
</script>

<template>
  <div class="CartItem">
    <div class="CartItem__Media">
      <img :src="item.image" :alt="item.title" width="100" />
    </div>
    <div class="CartItem__Body Stack Stack--tiny">
      <p class="CartItem__Title">
        <a :href="item.url">
          {{ formatProductTitle(item.product_title) }}
        </a>
      </p>
      <p
        class="CartItem__VariantTitle"
        v-if="!item.product_has_only_default_variant"
      >
        {{ item.variant_title }}
      </p>
      <div class="CartItem__Footer">
        <div
          class="CartItem__QuantitySelector"
          v-if="item.product_id !== 7322083655742"
        >
          <QuantityControl
            :value="item.quantity"
            :is-busy="isBusy"
            @change-quantity="updateQuantity"
          >
          </QuantityControl>
        </div>
        <div class="CartItem__QuantitySelector" v-else>
          <span class="QuantityControl">
            <button
              class="QuantityControl__Button QuantityControl__Decrease"
              disabled
            >
              -
            </button>
            <span class="QuantityControl__Value">{{ item.quantity }}</span>
            <button
              class="QuantityControl__Button QuantityControl__Increase"
              disabled
            >
              +
            </button>
          </span>
        </div>
        <button
          :disabled="isBusy"
          @click="emit('remove', item.key)"
          class="CartItem__RemoveButton"
        >
          <IconBin></IconBin>
        </button>
        <span class="CartItem__Price">
          {{ item.price === 0 ? 'FREE' : formatMoney(item.price) }}
        </span>
      </div>
      <div class="CartItem__SubscriptionSwitcher">
        <select
          v-if="isSubscription"
          :class="{ 'Button--is-disabled': isBusy }"
          :disabled="isBusy"
          @change="
            updateSellingPlan(($event.target as HTMLSelectElement).value)
          "
        >
          <option
            v-for="spa in subscriptionData?.variants[item.variant_id]
              .sellingPlanAllocations"
            :value="spa.sellingPlan.id"
            :selected="
              item.selling_plan_allocation &&
              item.selling_plan_allocation.selling_plan.id ===
                spa.sellingPlan.id
            "
            :key="spa.sellingPlanGroupId"
          >
            {{
              spa.sellingPlan.name
                .replace(/^delivery /i, '')
                .replace('1 ', '')
                .toLowerCase()
            }}
          </option>
          <option value="NONE">{{ t('item_switch_to_once_only') }}</option>
        </select>
        <button
          v-else-if="subscriptionOptions && subscriptionOptions.length > 0"
          class="Button Button--block Button--inverted"
          :class="{ 'Button--is-disabled': isBusy }"
          :disabled="isBusy"
          @click="
            subscriptionOptions &&
              subscriptionOptions.length > 0 &&
              emit(
                'subscribe',
                item.key,
                subscriptionOptions[0].sellingPlan.id.toString(),
              )
          "
        >
          <span
            v-if="subscriptionData && savePercent > 0"
            v-html="
              t('item_switch_to_subscription_save').replace(
                '{{percent}}',
                savePercent.toString(),
              )
            "
          >
          </span>
          <span v-else>{{ t('item_switch_to_subscription') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style>
:first-child > .CartItem {
  border-top: none;
  margin-top: 0;
}

.CartItem {
  display: flex;
  margin: calc(var(--grid-gap)) 0 0 0;
  border-top: 1px solid var(--color-lichen-100);
  padding: calc(var(--grid-gap)) 24px 0 24px;
}

.CartItem__Media {
  margin-right: 10px;
  flex-shrink: 0;
  width: 20%;

  & > * {
    border-radius: 3px;
  }
}

.CartItem__GiftImage {
  background-color: var(--color-lichen-25);
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
}

.CartItem__Body {
  flex-grow: 1;
}

.CartItem__Body--vertically-centered {
  display: flex;
  align-items: center;
}

.CartItem__Footer {
  display: flex;
  align-items: center;
  margin-top: 10px;
}

.CartItem__Title {
  flex-shrink: 1;
  flex-basis: 100%;
  margin-bottom: 0;

  & > a {
    color: var(--color-forest-100);
  }

  & + .CartItem__VariantTitle {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.CartItem__VariantTitle {
  flex-shrink: 1;
  flex-basis: 100%;
  color: var(--color-forest-60);
}

.CartItem__QuantitySelector {
  margin-right: 10px;
}

.CartItem__RemoveButton {
  color: var(--color-forest-100);
  appearance: none;
  border: none;
  background-color: transparent;
  height: 38px;
  width: 38px;
  text-align: left;
  cursor: pointer;

  & > svg {
    height: 16px;
    width: 16px;
  }
}

.CartItem__Price {
  flex-grow: 1;
  margin-left: 10px;
  text-align: right;
  font-weight: bold;
}
</style>
